import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Axios from 'axios';
import { useImmerReducer } from 'use-immer';
import { Alert, Grid, Button, Modal, TextField, Typography, IconButton, Snackbar } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'; 
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs'; 
import CloseIcon from '@mui/icons-material/Close';

import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";
import { centuryOptions } from '../../Constants/CenturyConstants';
import { formatDate } from '../../Utils/DateUtils';

const EditScientist = ({ open, onClose, scientist }) => {
    const { config, environment } = require('../../index');
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const GlobalState = useContext(StateContext); 
    const userId = GlobalState.userId;

    const initialState = {
        scientistFirstName: '',
        scientistLastName: '',
        scientistCentury: '',
        scientistThesis: '',
        scientistThesisDate: dayjs(),
        scientistResearchField: '',
        scientistBirthDate: dayjs(),
        scientistBirthPlace: '',
        scientistDeathDate: dayjs(),
        scientistDeathPlace: '',
        scientistSzekelyfoldConnection: '',
        fieldsOfScience: [],
        sendRequest: 0,
        openSnack: false,
        snackMessage: '', 
        disabledEditScientistBtn: false,
        FirstNameError: { hasError: false, errorMessage: '' },
        LastNameError: { hasError: false, errorMessage: '' },
        CenturyError: { hasError: false, errorMessage: '' },
        ResearchFieldError: { hasError: false, errorMessage: '' },
        SzekelyfoldConnectionError: { hasError: false, errorMessage: '' },
    };

    function ReducerFunction(draft, action) {
        switch (action.type) {
            case 'loadScientist':
                draft.scientistFirstName = action.payload.scientistfirstname || '';
                draft.scientistLastName = action.payload.scientistlastname || '';
                draft.scientistCentury = action.payload.centuryid ? (Number(action.payload.centuryid) + 2).toString() : '';
                draft.scientistThesis = action.payload.scientistthesistitle || '';
                draft.scientistThesisDate = action.payload.scientistthesisdate ? dayjs(action.payload.scientistthesisdate) : dayjs();
                draft.scientistResearchField = action.payload.fieldofscienceid || '';
                draft.scientistBirthDate = action.payload.scientistbirthdate ? dayjs(action.payload.scientistbirthdate) : dayjs();
                draft.scientistBirthPlace = action.payload.scientistbirthplace || '';
                draft.scientistDeathDate = action.payload.scientistdeathdate ? dayjs(action.payload.scientistdeathdate) : dayjs();
                draft.scientistDeathPlace = action.payload.scientistdeathplace || '';
                draft.scientistSzekelyfoldConnection = action.payload.scientistszeklertie || '';
                break;
            case 'catchFirstNameChange':
                draft.scientistFirstName = action.scientistFirstNameChosen;
                draft.FirstNameError = { hasError: false, errorMessage: '' };
                break;
            case 'catchLastNameChange':
                draft.scientistLastName = action.scientistLastNameChosen;
                draft.LastNameError = { hasError: false, errorMessage: '' };
                break;
            case 'catchCenturyChange':
                draft.scientistCentury = action.scientistCenturyChosen;
                draft.CenturyError = { hasError: false, errorMessage: '' };
                break;
            case 'catchThesisChange':
                draft.scientistThesis = action.scientistThesisChosen;
                break;
            case 'catchThesisDateChange':
                draft.scientistThesisDate = action.scientistThesisDateChosen;
                break;
            case 'catchResearchFieldChange':
                draft.scientistResearchField = action.scientistResearchFieldChosen;
                draft.ResearchFieldError = { hasError: false, errorMessage: '' };
                break;
            case 'catchBirthDateChange':
                draft.scientistBirthDate = action.scientistBirthDateChosen;
                break;
            case 'catchBirthPlaceChange':
                draft.scientistBirthPlace = action.scientistBirthPlaceChosen;
                break;
            case 'catchDeathDateChange':
                draft.scientistDeathDate = action.scientistDeathDateChosen;
                break;
            case 'catchDeathPlaceChange':
                draft.scientistDeathPlace = action.scientistDeathPlaceChosen;
                break;
            case 'catchSzekelyfoldConnectionChange':
                draft.scientistSzekelyfoldConnection = action.scientistSzekelyfoldConnectionChosen;
                draft.SzekelyfoldConnectionError = { hasError: false, errorMessage: '' };
                break;
            case 'catchFieldsOfScience':
                draft.fieldsOfScience = action.fieldsOfScience;
                break;
            case 'changeSendRequest':
                draft.sendRequest = draft.sendRequest + 1;
                draft.disabledEditScientistBtn = true;
                break;
            case 'openTheSnack':
                draft.openSnack = true;
                break;
            case 'allowTheEditScientistBtn':
                draft.disabledEditScientistBtn = false;
                break;
            case 'emptyFirstName':
                draft.FirstNameError = { hasError: true, errorMessage: t('Validation.EmptyFirstName', 'A keresztnév mező nem lehet üres') };
                break;
            case 'emptyLastName':
                draft.LastNameError = { hasError: true, errorMessage: t('Validation.EmptyLastName', 'A vezetéknév mező nem lehet üres') };
                break;
            case 'emptyCentury':
                draft.CenturyError = { hasError: true, errorMessage: t('Validation.EmptyCentury', 'Az évszázad mező nem lehet üres') };
                break;           
            case 'emptyResearchField':
                draft.ResearchFieldError = { hasError: true, errorMessage: t('Validation.EmptyResearchField', 'A kutatási terület mező nem lehet üres') };
                break;           
            case 'emptySzekelyfoldConnection':
                draft.SzekelyfoldConnectionError = { hasError: true, errorMessage: t('Validation.EmptyConnection', 'A székelyföldi kötődés mező nem lehet üres') };
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

    useEffect(() => {
        if (open && scientist) {
            dispatch({ type: 'loadScientist', payload: scientist });
        }
    }, [open, scientist, dispatch]);

    useEffect(() => {
        const fieldsOfScienceEndpoint = config[environment].fieldsOfScienceEndpoint;
        async function GetFieldsOfScience() {
            try {
                const response = await Axios.get(fieldsOfScienceEndpoint);
                dispatch({ type: 'catchFieldsOfScience', fieldsOfScience: response.data });
            } catch (e) {
                console.error(e);
            }
        }
        GetFieldsOfScience();
    }, [config, environment, dispatch]);

    useEffect(() => {
        if (state.sendRequest) {
            const data = new FormData();
            data.append('firstname', state.scientistFirstName);
            data.append('lastname', state.scientistLastName);
            data.append('century', (Number(state.scientistCentury) - 2).toString());
            data.append('thesis_title', state.scientistThesis);
            data.append('thesis_date', formatDate(state.scientistThesisDate));
            data.append('fields_of_science', state.scientistResearchField);
            data.append('birth_date', formatDate(state.scientistBirthDate));
            data.append('birth_place', state.scientistBirthPlace);
            data.append('death_date', formatDate(state.scientistDeathDate));
            data.append('death_place', state.scientistDeathPlace);
            data.append('szekler_tie', state.scientistSzekelyfoldConnection);
            data.append('contributor', userId);

            async function UpdateScientist() {
                try {
                    const updateScientistEndpoint = config[environment].updateScientistEndpoint(scientist.scientistid);
                    await Axios.patch(updateScientistEndpoint, data);
                    dispatch({ type: 'openTheSnack' });
                    window.location.reload();
                } catch (e) {
                    dispatch({ type: 'allowTheEditScientistBtn' });
                }
            }
            UpdateScientist();
        }
    }, [state.sendRequest, config, environment, userId, scientist?.scientistid, state.scientistFirstName, state.scientistLastName, state.scientistCentury, state.scientistThesis, state.scientistThesisDate, state.scientistResearchField, state.scientistBirthDate, state.scientistBirthPlace, state.scientistDeathDate, state.scientistDeathPlace, state.scientistSzekelyfoldConnection]);

    function FormSubmit(e) {
        e.preventDefault();
        if (state.scientistFirstName && state.scientistLastName && state.scientistCentury && state.scientistResearchField && state.scientistSzekelyfoldConnection) {
            dispatch({ type: 'changeSendRequest' });
        } else {
            if (!state.scientistFirstName) dispatch({ type: 'emptyFirstName' });
            if (!state.scientistLastName) dispatch({ type: 'emptyLastName' });
            if (!state.scientistCentury) dispatch({ type: 'emptyCentury' });
            if (!state.scientistResearchField) dispatch({ type: 'emptyResearchField' });
            if (!state.scientistSzekelyfoldConnection) dispatch({ type: 'emptySzekelyfoldConnection' });
        }
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Modal open={open} onClose={onClose}>
                <form onSubmit={FormSubmit}>
                    <Grid
                        container
                        direction="column"
                        alignItems="stretch"
                        justifyContent="center"
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90%',
                            maxWidth: 600,
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2
                        }}
                    >
                        <Grid container justifyContent="space-between">
                            <Typography variant='h5'>{t('Academy.EditScientist')}</Typography>
                            <IconButton onClick={onClose}><CloseIcon /></IconButton>
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    variant="standard"
                                    label={t('Academy.LastName')}
                                    value={state.scientistLastName}
                                    onChange={(e) => dispatch({ type: 'catchLastNameChange', scientistLastNameChosen: e.target.value })}
                                    error={state.LastNameError.hasError}
                                    helperText={state.LastNameError.errorMessage}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    variant="standard"
                                    label={t('Academy.FirstName')}
                                    value={state.scientistFirstName}
                                    onChange={(e) => dispatch({ type: 'catchFirstNameChange', scientistFirstNameChosen: e.target.value })}
                                    error={state.FirstNameError.hasError}
                                    helperText={state.FirstNameError.errorMessage}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    variant="standard"
                                    label={t('Academy.Century')}
                                    value={state.scientistCentury}
                                    onChange={(e) => dispatch({ type: 'catchCenturyChange', scientistCenturyChosen: e.target.value })}
                                    error={state.CenturyError.hasError}
                                    helperText={state.CenturyError.errorMessage}
                                    select
                                    SelectProps={{ native: true }}
                                    fullWidth
                                >
                                    {centuryOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    variant="standard"
                                    label={t('Academy.ResearchField')}
                                    value={state.scientistResearchField}
                                    onChange={(e) => dispatch({ type: 'catchResearchFieldChange', scientistResearchFieldChosen: e.target.value })}
                                    error={state.ResearchFieldError.hasError}
                                    helperText={state.ResearchFieldError.errorMessage}
                                    select
                                    SelectProps={{ native: true }}
                                    fullWidth
                                >
                                    <option value=""></option>
                                    {state.fieldsOfScience.map((option) => (
                                        <option key={option.id} value={option.id}>{option.name}</option>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="standard"
                                    label={t('Academy.ThesisTitle')}
                                    value={state.scientistThesis}
                                    onChange={(e) => dispatch({ type: 'catchThesisChange', scientistThesisChosen: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('Academy.ThesisDate')}
                                    value={state.scientistThesisDate}
                                    onChange={(newValue) => dispatch({ type: 'catchThesisDateChange', scientistThesisDateChosen: newValue })}
                                    slotProps={{ textField: { fullWidth: true, variant: 'standard' } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('Academy.BirthDate')}
                                    value={state.scientistBirthDate}
                                    onChange={(newValue) => dispatch({ type: 'catchBirthDateChange', scientistBirthDateChosen: newValue })}
                                    slotProps={{ textField: { fullWidth: true, variant: 'standard' } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    variant="standard"
                                    label={t('Academy.BirthPlace')}
                                    value={state.scientistBirthPlace}
                                    onChange={(e) => dispatch({ type: 'catchBirthPlaceChange', scientistBirthPlaceChosen: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('Academy.DeathDate')}
                                    value={state.scientistDeathDate}
                                    onChange={(newValue) => dispatch({ type: 'catchDeathDateChange', scientistDeathDateChosen: newValue })}
                                    slotProps={{ textField: { fullWidth: true, variant: 'standard' } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    variant="standard"
                                    label={t('Academy.DeathPlace')}
                                    value={state.scientistDeathPlace}
                                    onChange={(e) => dispatch({ type: 'catchDeathPlaceChange', scientistDeathPlaceChosen: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    variant="standard"
                                    label={t('Academy.SzekelyfoldConnection')}
                                    value={state.scientistSzekelyfoldConnection}
                                    onChange={(e) => dispatch({ type: 'catchSzekelyfoldConnectionChange', scientistSzekelyfoldConnectionChosen: e.target.value })}
                                    error={state.SzekelyfoldConnectionError.hasError}
                                    helperText={state.SzekelyfoldConnectionError.errorMessage}
                                    multiline
                                    rows={2}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="center" sx={{ mt: 3 }}>
                            <Button variant='contained' type='submit' disabled={state.disabledEditScientistBtn}>
                                {t('Common.Save', 'MENTÉS')}
                            </Button>
                        </Grid>
                    </Grid>

                    <Snackbar open={state.openSnack} autoHideDuration={6000}>
                        <Alert severity="success" sx={{ width: '100%' }}>{t('Academy.ScientistUpdated', 'Tudós sikeresen frissítve!')}</Alert>
                    </Snackbar>
                </form>
            </Modal>
        </LocalizationProvider>
    );
};

export default EditScientist;
